import { regex, required } from 'react-admin';

/**
 * Regex validator for exactly 3 capitalized letters
 * The pattern uses lookahead assertions to ensure there are:
 * - Exactly 3 capitalized letters (not more, not less)
 * 
 * @example
 * // Valid: 'ABC1234', 'A1B2C34', '1234ABC'
 * // Invalid: 'abc1234', 'Ab1C234', 'ABCD1234', '444'
 */
export const containsThreeCapitalLetters = regex(
  /^(?=(?:.*[A-Z]){3})(?!(?:.*[A-Z]){4})(?!.*[a-z]).*$/,
  'Codes must have exactly 3 capitalized letters and 4 numbers'
);

/**
 * Regex validator for exactly 4 numbers
 * The pattern uses lookahead assertions to ensure there are:
 * - Exactly 4 numbers (not more, not less)
 * 
 * @example
 * // Valid: 'ABC1234', 'A1B2C34', '1234ABC'
 * // Invalid: 'ABC123', 'ABC12345', 'ABCDEF'
 */
export const containsFourNumbers = regex(
  /^(?=(?:.*[0-9]){4})(?!(?:.*[0-9]){5}).*$/,
  'Codes must have exactly 3 capitalized letters and 4 numbers'
);

/**
 * Combined validators for code format (3 capitalized letters and 4 numbers)
 * This validator array can be used directly or spread into other validator arrays
 */
export const validateCodeFormat = [
  containsThreeCapitalLetters, 
  containsFourNumbers,
];

/**
 * Combined validators for code name field (required + format validation)
 * This includes both the required validator and the format validators
 */
export const validateCodeName = [required('Code name is required'), ...validateCodeFormat];

/**
 * A custom error formatter for displaying a unified error message
 * This will override the individual error messages for a better user experience
 */
export const formatCodeError = (error) => {
  if (error && (
    error.includes('capitalized letters') || 
    error.includes('numbers')
  )) {
    return 'Codes must have exactly 3 capitalized letters and 4 numbers';
  }
  return error;
};

/**
 * Other code-related validators
 */
export const validateOrganization = [required('Organization is required')];
export const validateCodeType = [required('Code type is required')];

/**
 * Validates if a code value meets the requirement of exactly 3 capital letters and 4 numbers
 * 
 * @param {string} value - The code value to validate
 * @returns {boolean} Whether the code meets all requirements
 */
export const isValidCode = (value) => {
  if (!value) return false;
  
  const hasThreeCapitalLetters = (value.match(/[A-Z]/g) || []).length === 3;
  const hasFourNumbers = (value.match(/[0-9]/g) || []).length === 4;
  const hasNoLowercase = !/[a-z]/.test(value);
  
  return hasThreeCapitalLetters && hasFourNumbers && hasNoLowercase;
}; 
